import { FlowType } from "../components/technical/upload";
import { getRequest, postRequest } from "../utils/fetch";

const ecomApiPath = window.context.ecomApiUrl;

export interface IUploadResult {
	readonly success: boolean;
	readonly file: File;
}

export enum ImportStatus {
	Waiting = 0,
	Imported = 1,
	Failed = 2,
	NotFound = 3,
	LoggedOut = 4,
}

export interface IUploadSessionRequest {
	pointOfSaleId: string;
	folderId: string;
	flowType: FlowType;
	uploadTechnology: string;
	accountId: string;
}

export interface IUploadSessionResponse {
	readonly id: string;
	readonly folderId: string;
	readonly uploadUrl: string;
	readonly uploadUrlRaw: string;
}

export interface IUploadSessionStatus {
	readonly fileId: string;
	readonly status: ImportStatus;
	readonly info: string;
	readonly originalFileName: string;
}

export async function upload(path: string, file: File, config?: RequestInit): Promise<IUploadResult> {
	const formData = new FormData();
	formData.append("file", file);
	formData.append("fileName", file.name);

	const init = { method: "post", body: formData, ...config };
	const request = new Request(path.toLowerCase(), init);
	try {
		const response = await fetch(request);
		return { success: response.ok, file: file } as IUploadResult;
	} catch (err) {
		return { success: false, file: file } as IUploadResult;
	}
}

export async function uploadRaw(
	path: string,
	sessionId: string,
	file: File,
	config?: RequestInit,
): Promise<IUploadResult> {
	const init = { method: "post", body: file, ...config };
	const request = new Request(path.toLowerCase(), init);

	request.headers.set("Content-Type", "application/octet-stream");
	request.headers.append("X-EXP-UploadSessionId", sessionId);
	request.headers.append("X-EXP-UploadFilename", file.name);

	try {
		const response = await fetch(request);
		return { success: response.ok, file: file } as IUploadResult;
	} catch (err) {
		return { success: false, file: file } as IUploadResult;
	}
}

export function createUploadSessionAsync(
	folderId: string,
	flowType: FlowType,
	uploadTechnology: string,
	accountId: string,
): Promise<IUploadSessionResponse> {
	const request: IUploadSessionRequest = {
		pointOfSaleId: window.context.pointOfSaleId,
		folderId,
		flowType,
		uploadTechnology,
		accountId,
	};

	return postRequest<IUploadSessionRequest, IUploadSessionResponse>(
		ecomApiPath + "/mediastore/accounts/" + accountId + "/upload/session",
		request,
		{
			headers: new Headers({ "Content-type": "application/json; charset=utf-8" }),
		},
	);
}

export function getUploadSessionAsync(sessionId: string, accountId: string): Promise<IUploadSessionResponse> {
	return getRequest<IUploadSessionResponse>(
		ecomApiPath + "/mediastore/accounts/" + accountId + "/upload/session/" + sessionId,
		{
			headers: new Headers({ "Content-type": "application/json; charset=utf-8" }),
		},
	);
}

export function checkImportStatusForSessionAsync(
	sessionId: string,
	accountId: string,
): Promise<IUploadSessionStatus[]> {
	return getRequest<IUploadSessionStatus[]>(
		ecomApiPath + "/mediastore/accounts/" + accountId + "/upload/session/" + sessionId + "/status",
		{ headers: new Headers({ "Content-type": "application/json; charset=utf-8" }) },
	);
}
